import { create } from 'zustand';
import { Challenge, challengesApi } from '@/api/challenges';

interface ChallengeStore {
  challenges: Challenge[];
  selectedChallenge: Challenge | null;

  // My Challenges data
  myActiveChallenges: Challenge[];
  myUpcomingChallenges: Challenge[];
  myCompletedChallenges: Challenge[];

  isLoading: boolean;
  error: string | null;

  // Actions
  fetchChallenges: () => Promise<void>;
  fetchChallenge: (id: string) => Promise<void>;
  createChallenge: (data: any) => Promise<Challenge>;
  joinChallenge: (id: string, stakePaid: number) => Promise<void>;
  leaveChallenge: (id: string) => Promise<void>;

  // My Challenges actions
  fetchMyActiveChallenges: () => Promise<void>;
  fetchMyUpcomingChallenges: () => Promise<void>;
  fetchMyCompletedChallenges: () => Promise<void>;
  fetchAllMyChallenges: () => Promise<void>;

  setSelectedChallenge: (challenge: Challenge | null) => void;
  clearError: () => void;
}

export const useChallengeStore = create<ChallengeStore>((set, get) => ({
  challenges: [],
  selectedChallenge: null,

  // My Challenges initial state
  myActiveChallenges: [],
  myUpcomingChallenges: [],
  myCompletedChallenges: [],

  isLoading: false,
  error: null,

  fetchChallenges: async () => {
    set({ isLoading: true, error: null });
    try {
      const challenges = await challengesApi.getChallenges();
      set({ challenges, isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch challenges', isLoading: false });
    }
  },

  fetchChallenge: async (id: string) => {
    // If fetching a DIFFERENT challenge, clear stale data immediately
    // If fetching the SAME challenge (refresh), keep showing it while loading (stale-while-revalidate)
    const currentChallenge = get().selectedChallenge;

    if (currentChallenge?.id !== id) {
      // Different challenge - clear stale data to prevent flash of wrong content
      set({ selectedChallenge: null, isLoading: true, error: null });
    } else {
      // Same challenge - keep showing while refreshing
      set({ isLoading: true, error: null });
    }

    try {
      const challenge = await challengesApi.getChallenge(id);
      set({ selectedChallenge: challenge, isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch challenge', isLoading: false });
    }
  },

  createChallenge: async (data: any) => {
    set({ isLoading: true, error: null });
    try {
      const challenge = await challengesApi.createChallenge(data);
      set((state) => ({
        challenges: [challenge, ...state.challenges],
        isLoading: false,
      }));
      return challenge;
    } catch (error: any) {
      set({ error: error.message || 'Failed to create challenge', isLoading: false });
      throw error;
    }
  },

  joinChallenge: async (id: string, stakePaid: number) => {
    set({ isLoading: true, error: null });
    try {
      await challengesApi.joinChallenge(id, stakePaid);
      // Refresh the challenge data
      await get().fetchChallenge(id);
      await get().fetchMyActiveChallenges();
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to join challenge', isLoading: false });
      throw error;
    }
  },

  leaveChallenge: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await challengesApi.leaveChallenge(id);
      await get().fetchMyActiveChallenges();
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to leave challenge', isLoading: false });
      throw error;
    }
  },

  // Fetch user's active challenges
  fetchMyActiveChallenges: async () => {
    set({ isLoading: true, error: null });
    try {
      const myActiveChallenges = await challengesApi.getMyActiveChallenges();
      set({ myActiveChallenges, isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch active challenges', isLoading: false });
    }
  },

  // Fetch user's upcoming challenges
  fetchMyUpcomingChallenges: async () => {
    set({ isLoading: true, error: null });
    try {
      const myUpcomingChallenges = await challengesApi.getMyUpcomingChallenges();
      set({ myUpcomingChallenges, isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch upcoming challenges', isLoading: false });
    }
  },

  // Fetch user's completed challenges
  fetchMyCompletedChallenges: async () => {
    set({ isLoading: true, error: null });
    try {
      const myCompletedChallenges = await challengesApi.getMyCompletedChallenges();
      set({ myCompletedChallenges, isLoading: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch completed challenges', isLoading: false });
    }
  },

  // Fetch all my challenges at once (helper for loading all tabs)
  fetchAllMyChallenges: async () => {
    set({ isLoading: true, error: null });
    try {
      const [myActiveChallenges, myUpcomingChallenges, myCompletedChallenges] = await Promise.all([
        challengesApi.getMyActiveChallenges(),
        challengesApi.getMyUpcomingChallenges(),
        challengesApi.getMyCompletedChallenges(),
      ]);
      set({
        myActiveChallenges,
        myUpcomingChallenges,
        myCompletedChallenges,
        isLoading: false,
      });
    } catch (error: any) {
      console.error('❌ [STORE] Error fetching challenges:', error);
      set({ error: error.message || 'Failed to fetch my challenges', isLoading: false });
    }
  },

  setSelectedChallenge: (challenge: Challenge | null) => {
    set({ selectedChallenge: challenge });
  },

  clearError: () => {
    set({ error: null });
  },
}));
